import React, { useCallback } from 'react';
import classnames from 'classnames';
import axios from 'axios';
import auth from '../utils/auth';

import { formatCurrency, sorts } from '../utils';
import { default as cartUtils } from '../utils/cart';

// Components
import { Link, useParams } from 'react-router-dom';
import ProductTable from '../components/ProductTable.jsx';
import Totals from '../components/checkout/Totals.jsx';
import Loading from '../components/Loading.jsx';
import Error from './Error.jsx';
import ProductSearch from '../components/ProductSearch.jsx';
import PromoEntry from '../components/checkout/PromoEntry.jsx';

import { saveCart, getWarranties, createProposal } from '../actions/cart';

import { getInitials, getDisplayName } from '../utils/userUtils';
import FormattedPlural from '../components/FormattedPlural';
import AddSubmitterButton from '../components/product/AddSubmitterButton';
import Avatar from '../components/Avatar';
import Modal from '../components/Modal';
import { Import } from '../components/checkout/Import';
import { loaders } from '../utils/loaders';
import Input from '../components/form/Input';
import Alert from 'react-s-alert';
import { useUser } from './User';
import { useSelector } from 'react-redux';
import { userPermissions } from './UserDashboard';
const _debounce = require('lodash.debounce');

export const getCustomers = async (orgId) => {
    let { data } = await axios.request({
        url: `/entities/${orgId}/customers`,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.getToken()}`
        }
    });
    return data;
};

export const getCartById = async (userId, cartId) => {
    let resp = await axios.request({
        url: `/customers/${userId}/carts/${cartId}`,
        method: 'GET',
        headers: {
            'Content-Type': 'application/json',
            'Session-Key': auth.getSID()
        }
    });
    return resp;
};

const CartView = () => {
    const params = useParams();
    const session = useSelector((state) => state.session);
    const { user, org, grids, sop } = useUser(params.user_id);
    const [inProgress, setInProgress] = React.useState(true);
    const [error, setError] = React.useState(false);
    const [users, setUsers] = React.useState([]);
    const [carts, setCarts] = React.useState([]);
    const [cart, setCart] = React.useState({ items: [] });
    const [addresses, setAddresses] = React.useState([]);
    const [proposalUrl, setProposalUrl] = React.useState(null);
    const [creatingProposal, setCreatingProposal] = React.useState(false);
    const [poNumber, setPoNumber] = React.useState('');
    const [warranties, setWarranties] = React.useState([]);
    const [showSubmittersModal, setShowSubmittersModal] = React.useState(false);

    const refreshCart = useCallback(async () => {
        try {
            const resp = await getCartById(params.user_id, params.cart_id);
            setInProgress(false);
            setCart(resp.data);
            setPoNumber(resp?.data?.po_number || '');
        } catch (error) {
            console.error(error);
            setInProgress(false);
            setError(true);
        }
    }, [params.user_id, params.cart_id]);

    React.useEffect(() => {
        (async () => {
            let carts = await loaders.user_carts.load(params.user_id);
            let addresses = await loaders.user_addresses.load(params.user_id);
            setCarts(carts);
            setAddresses(addresses);
            await refreshCart();
            const wResp = await getWarranties();
            const users = await getCustomers(user.netsuite_entity);
            setWarranties(wResp.data.data);
            setUsers(users);
        })();
    }, [params.user_id, user.netsuite_entity, refreshCart]);

    const handleClearCart = () => {
        let newCart = {
            items: [],
            promos_applied: [],
            po_number: '',
            notes: ''
        };
        setCart((cart) => ({
            ...cart,
            ...newCart
        }));
        saveCart(params.user_id, params.cart_id, newCart);
    };

    const handleAllocationChange = (cartId, sku, data) => {
        let items = cart.items.map((item) => {
            if (item.sku === sku) {
                item.allocations = data;
            }
            return item;
        });

        setCart((cart) => ({
            ...cart,
            items
        }));
        saveCart(params.user_id, params.cart_id, {
            items: cartUtils.getItemsStub(cart.items)
        });
    };

    const handleQtyChange = async (sku, value) => {
        const prevCart = { ...cart };
        let items = cart.items.map((item) => {
            if (item.sku === sku) {
                item.qty = parseInt(value, 10);
            }
            return item;
        });

        setCart((cart) => ({
            ...cart,
            items
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                items: cartUtils.getItemsStub(cart.items)
            });
            await refreshCart();
        } catch (error) {
            Alert.error(`Error Updating quantity.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleApplyPromo = (data) => {
        setCart(data);
    };

    const handleItemRemove = async (product) => {
        const prevCart = { ...cart };
        let items = cart.items.filter((item) => item.sku !== product.sku);

        setCart((cart) => ({
            ...cart,
            items
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                items: cartUtils.getItemsStub(items)
            });
            await refreshCart();
        } catch (error) {
            Alert.error(`Error removing item.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handlePkgSelect = async (item, pkgType) => {
        if (!item) return;
        const prevCart = { ...cart };
        let items = [...(cart?.items || [])];
        let _newItem = { ...item };

        if (pkgType) {
            _newItem.pkg_type = pkgType;
            _newItem.sku = _newItem.packaging_data[pkgType].sku;
            _newItem.isbn = _newItem.packaging_data[pkgType].isbn;
        }

        let _item = items.find((i) => i.sku === _newItem.sku);

        if (_item) {
            _item.qty = item.qty + (_newItem.qty ? _newItem.qty : 1);
        } else {
            items = [...items.filter((i) => i.sku !== item.sku), _newItem];
        }

        setCart((cart) => ({ ...cart, items }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                items: cartUtils.getItemsStub(items)
            });
            await refreshCart();
        } catch (error) {
            Alert.error(`Error setting package type.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleItemAdd = async (newItem) => {
        if (!newItem) return;
        let prevCart = { ...cart };
        let items = [...cart?.items];
        let _newItem = { ...newItem };

        let item = items.find((i) => i.sku === _newItem.sku);

        if (item) {
            item.qty = item.qty + (_newItem.qty ? _newItem.qty : 1);
        } else {
            items = [...items, _newItem];
        }

        setCart((cart) => ({
            ...cart,
            items
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                items: cartUtils.getItemsStub(items)
            });
            await refreshCart();
        } catch (error) {
            Alert.error(`Error setting package type.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleUpdateWarranty = async ({ sku, format, qty }, includeWarranty) => {
        const prevCart = { ...cart };
        let items = cart.items.map((item) => {
            if (item.sku === sku) {
                item.include_warranty = includeWarranty;
            }
            return item;
        });

        let _warranty = warranties.find((w) => w.format === `warranty-${format}`);
        let _cartWarranty = cart.items.find((w) => w.format === `warranty-${format}`);
        if (_warranty && !_cartWarranty && includeWarranty) {
            items.push({
                ..._warranty,
                qty
            });
        }

        setCart((cart) => ({
            ...cart,
            items
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                items: cartUtils.getItemsStub(cart.items)
            });
            await refreshCart();
        } catch (error) {
            Alert.error(`Error updating warranty.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleAddSubmitter = async (submitter) => {
        const prevCart = { ...cart };
        let submitters = cart?.additional_submitters || [];
        if (submitter !== 'clear' && submitters?.map((sub) => sub._id).includes(submitter._id)) return false;

        let newSubmitters = [...submitters, submitter];

        if (submitter === 'clear') {
            newSubmitters = [];
        }

        setCart((cart) => ({
            ...cart,
            additional_submitters: newSubmitters
        }));

        try {
            await saveCart(params.user_id, params.cart_id, {
                additional_submitters: newSubmitters.map((sub) => sub._id)
            });
        } catch (error) {
            Alert.error(`Error adding submitter.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleRemoveSubmitter = async (submitter) => {
        const prevCart = { ...cart };
        let submitters = cart.additional_submitters;
        let newSubmitters = submitters.filter((user) => user._id !== submitter);
        setCart((cart) => ({
            ...cart,
            additional_submitters: newSubmitters
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                additional_submitters: newSubmitters.map((sub) => sub._id)
            });
        } catch (error) {
            Alert.error(`Error removing submitter.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleMakePublic = async () => {
        const prevCart = { ...cart };
        setCart((cart) => ({
            ...cart,
            is_shared: true,
            additional_submitters: [],
            netsuite_entity: org.entity_id
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                additional_submitters: [],
                is_shared: true,
                netsuite_entity: org.entity_id
            });
        } catch (error) {
            Alert.error(`Error making cart public.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleMakePrivate = async () => {
        const prevCart = { ...cart };
        setCart((cart) => ({
            ...cart,
            is_shared: false,
            additional_submitters: [],
            netsuite_entity: org.entity_id
        }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                additional_submitters: [],
                is_shared: false,
                netsuite_entity: org.entity_id
            });
        } catch (error) {
            Alert.error(`Error making cart public.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };

    const handleAddToCart = async (items = []) => {
        const prevCart = { ...cart };
        setCart((cart) => ({ ...cart, items }));
        try {
            await saveCart(params.user_id, params.cart_id, {
                items: cartUtils.getItemsStub(items)
            });
            await refreshCart();
        } catch (error) {
            Alert.error(`Error adding items to cart.`);
            Alert.error(error.message);
            setCart(prevCart);
        }
    };
    const generateProposal = async () => {
        let billingAddress = addresses?.find((adr) => adr.primary_billing);
        let products = cart.items.sort((a, b) => sorts.asc(a.title, b.title));
        if (!billingAddress) {
            Alert.error('You need a primary billing address set before creating a proposal.');
            return;
        }
        setCreatingProposal(true);
        try {
            let { data } = await createProposal({
                contact_name: `${user.name.given} ${user.name.family}`,
                now: Date.now(),
                date: new Date().toLocaleDateString('en-US'),
                email: user.email,
                phone: user.phone,
                items: products.map((i) => ({
                    title: i.title,
                    price: i.price,
                    isbn: i.isbn || 'n/a',
                    qty: i.qty
                })),
                po_number: poNumber,
                notes: cart.notes,
                sub_total: formatCurrency(cartUtils.getSubtotal(products, org.currency)),
                total: formatCurrency(
                    cartUtils.getGrandTotal(products, org.price_contracts, !cart.is_pif, org.currency)
                ),
                discount: formatCurrency(cartUtils.getDiscount(products, org.price_contracts, org.currency)),
                shipping: formatCurrency(cartUtils.getShipping(products, cart.is_pif, org.currency)),
                street: billingAddress.street,
                city: billingAddress.city,
                state: billingAddress.region.split('-')[1],
                zip: billingAddress.postal_code
            });
            setCreatingProposal(false);
            setProposalUrl(data.url);
        } catch (error) {
            console.error(error);
            Alert.error('Error Creating Proposal.');
            setCreatingProposal(false);
            setProposalUrl(null);
        }
    };

    const throttledSave = _debounce(async (key, value) => {
        await saveCart(params.user_id, params.cart_id, {
            [key]: value
        });
    }, 350);

    const handlePONumberChange = async (val) => {
        let value = val?.slice(0, 40) || '';
        let isSame = value === poNumber;
        setPoNumber(value);
        if (isSame) return;
        await throttledSave('po_number', poNumber);
    };
    if (inProgress) {
        return <Loading />;
    }

    if (error) {
        return <Error />;
    }
    let products = cart.items.sort((a, b) => sorts.asc(a.title, b.title));

    let isSopCart = !!cart.sop_id;

    let sub = null;
    let emptyRowCount = 0;

    if (isSopCart) {
        sub = sop.find((sub) => sub.internal_id === parseInt(cart.sop_id, 10));
        emptyRowCount = sub.quantity - cartUtils.getUnitCount(cart.items);
    }

    let unitCount = cartUtils.getUnitCount(products);
    let applyPromo = cartUtils.isPromoMonth(sub);
    let services = cart.items.filter((i) => i.format && i.format.includes('service'));

    // get a list of cart items excluding services.
    // sort descending by price.
    let items = cart.items
        .filter((i) => i.format && !i.format.includes('service') && !i.format.includes('warranty'))
        .sort((a, b) => sorts.desc(a.sku, b.sku))
        .sort((a, b) => sorts.desc(a.price, b.price));

    // get a new list of items we should charge for.
    // if its a promo month only calculate totals
    // for the subscription quantity + services
    // otherwise include everytyhing.
    items = applyPromo ? [...cartUtils.getChargedItems(items, sub.quantity), ...services] : cart.items;

    let isReadOnly = (session?.permissions || []).includes(userPermissions.READONLY);

    let title = cart.is_pif
        ? 'Play it Forward Cart'
        : cart.is_wishlist
        ? `${cart.name ? cart.name : 'None'} [${cart._id}]`
        : cart.sop_id
        ? 'SOP Queue'
        : 'Shopping Cart';

    let cartUser = user;
    let submitters = [cartUser, ...(cart?.additional_submitters || [])];

    return (
        <div>
            <div className="row f-align-center">
                <div className="col-xs-12 col-md-6">
                    <h1>{title}</h1>
                    <p>
                        You have <strong>{unitCount}</strong> {unitCount > 1 ? 'units' : 'unit'} in your {title}.
                    </p>
                </div>

                {!isReadOnly && (
                    <div className="col-xs-12 col-md-6 end-md">
                        {proposalUrl && (
                            <a href={proposalUrl} target="_blank" rel="noopener noreferrer" className="ml-1 mr-2">
                                <small>Download Proposal</small>
                            </a>
                        )}
                        <button
                            type="button"
                            className="button full-xs auto-md spaced-double-xs collapse-md"
                            onClick={generateProposal}
                            disabled={creatingProposal || !cart.items.length}
                        >
                            Create Proposal
                        </button>
                        <button
                            type="button"
                            className="button full-xs auto-md spaced-double-xs collapse-md"
                            onClick={handleClearCart}
                        >
                            Clear Cart
                        </button>{' '}
                        <Link
                            to={`/customer/${params.user_id}/carts/${params.cart_id}/checkout`}
                            className={classnames(
                                'button button-primary full-xs auto-md spaced-double-xs collapse-md',
                                {
                                    'button-pif': cart.is_pif
                                }
                            )}
                        >
                            Checkout
                        </Link>
                    </div>
                )}
            </div>

            <div className="row">
                <div className="col-xs-12 col-lg-6 first-lg">
                    <div className="py-xs-3 py-md-0">
                        {cart.is_shared && !cartUser && (
                            <div className="alert flash-error l-text-left m-0">
                                <p>
                                    The user who owns this cart is no-longer a part of your library/school, please{' '}
                                    <a href="#olark" className="js-olark-expand">
                                        contact customer service
                                    </a>{' '}
                                    to request transferring ownership.
                                </p>
                            </div>
                        )}
                        {!isReadOnly && cart.is_shared && cartUser && (
                            <>
                                <span className="mr-2">Approved Submitters</span>
                                {submitters
                                    .filter(Boolean)
                                    .slice(0, 2)
                                    .map((user) => (
                                        <Avatar
                                            key={`avatar_${user._id}`}
                                            name={getInitials(getDisplayName(user))}
                                            label={user._id === cartUser._id ? 'You' : getDisplayName(user)}
                                            size={32}
                                            className="mr-2"
                                            corners="circle"
                                            closeable={user._id !== cartUser._id}
                                            onClose={(e) => handleRemoveSubmitter(user._id)}
                                        />
                                    ))}

                                {submitters.length > 2 && (
                                    <Avatar
                                        name={`${submitters.length - 2}+`}
                                        label={
                                            <>
                                                {submitters.length - 2} additional{' '}
                                                <FormattedPlural
                                                    value={submitters.length - 2}
                                                    one="submitter"
                                                    other="submitters"
                                                />
                                            </>
                                        }
                                        size={32}
                                        className="mr-2"
                                        corners="circle"
                                        closeable={false}
                                        clickable
                                        onClick={() => setShowSubmittersModal(true)}
                                    />
                                )}

                                <AddSubmitterButton
                                    options={users
                                        .filter(
                                            (user) =>
                                                user._id !== cart.customer_id &&
                                                !cart.additional_submitters.map((u) => u._id).includes(user._id) &&
                                                user.last_login
                                        )
                                        .sort((a, b) => sorts.desc(a.last_login, b.last_login))
                                        .map((user) => ({
                                            ...user,
                                            fullName: getDisplayName(user)
                                        }))}
                                    activeSubmitters={cart.additional_submitters}
                                    onItemSelect={handleAddSubmitter}
                                    buttonClassName="button-small"
                                />

                                <button type="button" className="button button-small" onClick={handleMakePrivate}>
                                    Make cart private
                                </button>
                            </>
                        )}
                        {!cart.is_shared && (
                            <button type="button" className="button button-small" onClick={handleMakePublic}>
                                Make cart public
                            </button>
                        )}

                        {cart.pre_populated && <strong style={{ whiteSpace: 'nowrap' }}>Created By FINDAWAY.</strong>}
                    </div>
                </div>
            </div>

            <section>
                <ProductTable
                    account={{
                        info: user || {},
                        grid: grids || {},
                        org: org || {}
                    }}
                    products={products}
                    isCart={true}
                    ilsEnabled={user.ils_enabled}
                    cartId={params.cart_id}
                    showListPrice={!isSopCart}
                    showImages={true}
                    showAllocations={user.ils_enabled}
                    showAudience={isSopCart}
                    showTotal={true}
                    showCheckbox={false}
                    showAllocTotal={true}
                    showAllocHeader={true}
                    emptyRowCount={emptyRowCount}
                    priceContracts={org.price_contracts}
                    onAllocChange={handleAllocationChange}
                    territory={user.territory}
                    currency={org.currency}
                    onQtyChange={handleQtyChange}
                    onItemRemove={handleItemRemove}
                    onWarrantySelect={handleUpdateWarranty}
                    onWarrantyRemove={handleUpdateWarranty}
                    onAddItem={handleItemAdd}
                    onPkgSelect={handlePkgSelect}
                    isReadOnly={isReadOnly}
                />

                {!isReadOnly && (
                    <div className="spaced-double">
                        <h4>Add Items</h4>
                        <ProductSearch onItemSelect={handleItemAdd} territory={user.territory || 'US'} />
                    </div>
                )}
            </section>
            {!isReadOnly && !!cart && (
                <Import
                    account_info={user}
                    addresses={addresses}
                    orgId={org.entity_id}
                    sop={sop}
                    cart={cart}
                    carts={carts}
                    session={session}
                    handleAddItemsToCart={handleAddToCart}
                />
            )}

            <div className="row spaced-double">
                <div className="col-xs-12 col-md-5 col-md-offset-7">
                    {!cart.is_pif && isReadOnly && (
                        <div>
                            <strong>Promotion Code:</strong> {!!cart?.promos_applied?.length && cart.promos_applied[0]}
                        </div>
                    )}
                    {!isReadOnly && !cart.is_pif && !isReadOnly && (
                        <PromoEntry
                            promos={cart.promos_applied}
                            userId={user._id}
                            cartId={cart._id}
                            onPromoUpdate={handleApplyPromo}
                        />
                    )}

                    <Totals
                        sub_total={formatCurrency(cartUtils.getSubtotal(items))}
                        shipping={formatCurrency(cartUtils.getShipping(items, cart.is_pif))}
                        grand_total={formatCurrency(cartUtils.getGrandTotal(items, org.price_contracts, !cart.is_pif))}
                        discount={formatCurrency(cartUtils.getDiscount(items, org.price_contracts))}
                        warranties={formatCurrency(
                            cartUtils.getSubtotal(
                                products.filter((p) => p.format && p.format.includes('warranty')),
                                org.currency
                            )
                        )}
                        unit_count={cartUtils.getUnitCount(products)}
                        promos={cart.promos_applied}
                        currency={org.currency}
                        onQtyChange={handleQtyChange}
                        onItemRemove={handleItemRemove}
                    />
                    <div>
                        <Input
                            label={
                                <>
                                    PO Number <small>(max 40 chars)</small>
                                </>
                            }
                            type="text"
                            name="po_number"
                            id="po_number"
                            disabled={cart.isReadOnly}
                            value={poNumber}
                            onChange={handlePONumberChange}
                        />
                    </div>
                    {!isReadOnly && (
                        <div className="l-text-right">
                            <Link
                                to={`/customer/${params.user_id}/carts/${params.cart_id}/checkout`}
                                className={classnames('button button-primary full-xs auto-md', {
                                    'button-pif': cart.is_pif
                                })}
                            >
                                Checkout
                            </Link>
                        </div>
                    )}
                </div>
            </div>
            <Modal visible={showSubmittersModal} onClose={() => setShowSubmittersModal(false)} innerClassName="p-4">
                <h3>Additional Submitters</h3>
                <ul className="list-flat">
                    {submitters.slice(2).map((user) => (
                        <li key={user._id}>
                            <Avatar
                                key={`avatar_${user._id}`}
                                name={getInitials(getDisplayName(user))}
                                label={getDisplayName(user)}
                                size={25}
                                className="mr-2"
                                corners="circle"
                                onClose={(e) => handleRemoveSubmitter(user._id)}
                            />
                            <span className="mr-2">{getDisplayName(user)}</span>
                            <button
                                type="button"
                                className="button btn-sm button-danger"
                                onClick={(e) => handleRemoveSubmitter(user._id)}
                            >
                                Remove
                            </button>
                        </li>
                    ))}
                </ul>
            </Modal>
        </div>
    );
};

export default CartView;
